/**
 * Iter-229e — Admin Jobs V2, premium card-first event workspace.
 *
 * Defaults to a card grid (one card per *event*, multi-day shifts consolidated).
 * Calendar and List views are available via the toolbar toggle.
 *
 * Cards surface: client, event, location, date range, role, pay, fill rate,
 * applicants to review, staffed count, briefing completion, status — and a
 * 5-action row (View · Staffing · Briefing · Message · Send Reminder).
 *
 * Presentation-layer only. Reuses GET /api/admin/shifts.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Loader2, Plus, Filter, ChevronDown, ChevronRight,
  Calendar as CalIcon, LayoutGrid, List as ListIcon, MapPin,
  Users, ClipboardCheck, MessageSquare, Send, Eye, AlertCircle, RefreshCw, Layers,
  Trash2, CheckSquare, History,
} from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';

const fmtSeriesDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
import { toast } from 'sonner';
import InviteTalentModal from '../../components/InviteTalentModal';
import { parseLocalDate, formatDate, formatTimeRange } from '../../utils/timeFormat';
import EventDetailDrawer from '../../components/admin/EventDetailDrawer';
import ShareJobButton from '../../components/ShareJobButton';
import AiCategoryChipStrip from '../../components/AiCategoryChipStrip';
import StaffingIntelligencePanel from '../../components/StaffingIntelligencePanel';
import AiPriorityBriefing from '../../components/AiPriorityBriefing';
import { classifyShift, summarize as summarizeStaffing } from '../../utils/aiOpsEngine';

/* ---------------------------------------------------------- helpers --- */

// 2026-09 — Bucketing MUST use the admin's LOCAL calendar date, not UTC.
// `toISOString()` returns UTC, so an admin in a US timezone late in the evening
// would see "today" roll to tomorrow and legitimately-today/near events get
// mis-bucketed (hidden). Event dates are stored as local YYYY-MM-DD, so we
// compare against a local YYYY-MM-DD "today".
const localYMD = (dt) => {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
const today = () => localYMD(new Date());
const isoDays = (d, n) => { const x = parseLocalDate(d) || new Date(d); x.setDate(x.getDate() + n); return localYMD(x); };
const TODAY = today();
const TOMORROW = isoDays(TODAY, 1);
const END_OF_WEEK = isoDays(TODAY, 7);
const NEXT_WEEK_END = isoDays(TODAY, 14);

const BRIEFING_FIELDS = ['grooming', 'parking_info', 'contact_name', 'contact_phone', 'sent_home_policy', 'reporting_instructions'];

function bucketOf(first, last) {
  if (!first) return 'future';
  if (first <= TODAY && (last || first) >= TODAY) return 'today';
  if (first === TOMORROW) return 'tomorrow';
  if (first > TODAY && first <= END_OF_WEEK) return 'this_week';
  if (first > END_OF_WEEK && first <= NEXT_WEEK_END) return 'next_week';
  if (first > NEXT_WEEK_END) return 'future';
  return 'past';
}

function briefingPct(shift) {
  const b = shift?.event_briefing || {};
  const dressCode = shift?.dress_code;
  const present = BRIEFING_FIELDS.filter(f => b[f] && String(b[f]).trim()).length + (dressCode ? 1 : 0);
  return Math.round((present / (BRIEFING_FIELDS.length + 1)) * 100);
}

function groupShifts(shifts) {
  const groups = new Map();
  for (const s of shifts) {
    // Iter-401 (Phase 1 AI Ops) — user asked for ONE CARD PER SHIFT DATE.
    // Multi-day events like "Guardian Garage Floors · Aug 15" and
    // "Guardian Garage Floors · Aug 16" now render as separate cards so
    // each date has its own staffing intelligence + AI recommendation.
    // We still collapse multiple roles for the SAME event on the SAME
    // day into one card (that's the natural mental model — Aug 15 has
    // 2 Bartenders + 1 Captain, not three cards).
    const eventLabel = (s.event_title || s.title || s.venue_name || s.gig_title || '').trim().toLowerCase();
    const shiftDay = String(s.shift_date || '').trim();
    const baseKey = s.event_group_id
      ? `egid::${s.event_group_id}`
      : [
          s.client_id || s.client_user_id || 'unknown',
          eventLabel,
        ].join('|');
    const key = `${baseKey}::${shiftDay}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  }
  const arr = [];
  for (const [k, list] of groups) {
    list.sort((a, b) => (a.shift_date || '').localeCompare(b.shift_date || ''));
    const first = list[0];
    // Iter-230 #8a — API returns `talent_needed`, `confirmed_count`, and
    // `staffed_count`. Earlier we keyed on `workers_filled`/`workers_needed`
    // which don't exist in the response, so every event ended up with
    // needed=0 and got mis-labelled "Staffing Needed". Use the real fields
    // with fallbacks so fully-staffed events (Ivystone, Schauben & Co) read
    // as such.
    const filled = list.reduce((n, s) => n + (s.staffed_count ?? s.workers_filled ?? s.confirmed_count ?? 0), 0);
    const needed = list.reduce((n, s) => n + (s.talent_needed ?? s.workers_needed ?? s.openings ?? 0), 0);
    const applicants = list.reduce((n, s) => n + (s.applicant_count ?? s.applicants_count ?? 0), 0);
    const fd = list[0].shift_date;
    const ld = list[list.length - 1].shift_date;
    arr.push({
      key: k,
      client_name: first.client_display || first.client_name || first.client_company_name || first.company_name || first.client_user_email || 'Client',
      client_id: first.client_id,
      client_logo_url: first.client_logo_url || null,
      event_title: first.event_title || first.title || first.venue_name || first.gig_title || '(untitled event)',
      role_name: first.role_name || first.gig_role_name || first.role || '',
      venue: first.venue_name || '',
      city: first.city || '',
      state: first.state || '',
      hourly_rate: first.hourly_rate || first.talent_pay_rate || first.pay_rate || null,
      shifts: list,
      total_shifts: list.length,
      // Iter-310 — When the event_group_id is set, surface unique role
      // names + count so the card header can read "3 roles (Bartender,
      // Brand Ambassador, Captain)" instead of pretending there's a
      // single role.
      event_group_id: first.event_group_id || null,
      primary_shift_id: first.id || first.shift_id || null,
      role_names: Array.from(new Set(list.map(r => r.role_name || r.gig_role_name).filter(Boolean))),
      roles_count: new Set(list.map(r => r.role_id).filter(Boolean)).size || list.length,
      first_date: fd,
      last_date: ld,
      is_multi_day: list.length > 1,
      // PO-2026-06 — Multi-day series context from the backend (each day of a
      // multi-day gig is a separate shift row sharing a series_id).
      series_id: first.series_id || null,
      series_total: first.series_total || null,
      series_day_index: first.series_day_index || null,
      series_dates: first.series_dates || null,
      filled,
      needed,
      applicants,
      briefing_pct: briefingPct(first),
      bucket: bucketOf(fd, ld),
    });
  }
  arr.sort((a, b) => {
    // Iter-229i — operational chronological sort. Active/upcoming events
    // (today, tomorrow, this week, future) come first in true date order;
    // completed events (last_date < today) sink to the bottom, sorted
    // most-recently-completed first. This matches how operations teams
    // prep: prioritize what's coming up, not what's behind us.
    const aPast = (a.last_date || a.first_date || '') < TODAY;
    const bPast = (b.last_date || b.first_date || '') < TODAY;
    if (aPast !== bPast) return aPast ? 1 : -1;
    if (aPast) return (b.last_date || '').localeCompare(a.last_date || '');
    return (a.first_date || '').localeCompare(b.first_date || '');
  });
  return arr;
}

function groupByClient(events) {
  const m = new Map();
  for (const e of events) {
    const k = e.client_name;
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(e);
  }
  const groups = [...m.entries()].map(([client_name, items]) => {
    // Iter-229i — enforce chronological sort INSIDE each client group so
    // even when grouped by client, the upcoming-most event surfaces first.
    items.sort((a, b) => (a.first_date || '').localeCompare(b.first_date || ''));
    return {
      client_name,
      client_logo_url: items.find(e => e.client_logo_url)?.client_logo_url || null,
      items,
      earliest_date: items[0]?.first_date || '9999-12-31',
      total_events: items.length,
      total_shifts: items.reduce((n, e) => n + e.total_shifts, 0),
      total_filled: items.reduce((n, e) => n + e.filled, 0),
      total_needed: items.reduce((n, e) => n + e.needed, 0),
      total_applicants: items.reduce((n, e) => n + e.applicants, 0),
    };
  });
  // Iter-229i — same operational sort for client groups: clients with
  // active/upcoming events come first (earliest-first); clients whose
  // events are all completed sink to the bottom, sorted by most-recent
  // completion first.
  groups.sort((a, b) => {
    const aActive = a.items.some(e => (e.last_date || e.first_date || '') >= TODAY);
    const bActive = b.items.some(e => (e.last_date || e.first_date || '') >= TODAY);
    if (aActive !== bActive) return aActive ? -1 : 1;
    return (a.earliest_date || '').localeCompare(b.earliest_date || '');
  });
  return groups;
}

const BUCKETS = [
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'this_week', label: 'This Week' },
  { id: 'next_week', label: 'Next Week' },
  { id: 'future', label: 'Future' },
  { id: 'all', label: 'All' },
];

function fillTone(filled, needed) {
  if (!needed) return 'slate';
  if (filled >= needed) return 'emerald';
  if (filled >= needed * 0.6) return 'amber';
  return 'rose';
}

/* Event status workflow — 7 stages, computed from data */
function eventStatus(event) {
  const isPast = (event.last_date || '') < TODAY;
  const isLive = (event.first_date || '') <= TODAY && (event.last_date || event.first_date || '') >= TODAY;
  if (isPast) return { id: 'completed',         label: 'Completed',           tone: 'slate'   };
  if (isLive) return { id: 'in_progress',       label: 'In Progress',         tone: 'cyan'    };
  const startsIn48h = (() => {
    const sd = parseLocalDate(event.first_date);
    if (!sd) return false;
    return (sd - new Date()) <= 48 * 3600 * 1000;
  })();
  const isFull = event.needed > 0 && event.filled >= event.needed;
  if (isFull && startsIn48h)             return { id: 'ready',               label: 'Ready for Event',     tone: 'emerald' };
  if (isFull)                            return { id: 'fully_staffed',       label: 'Fully Staffed',       tone: 'emerald' };
  if (event.applicants > 0 && event.filled === 0)
                                         return { id: 'reviewing',           label: 'Reviewing Applicants', tone: 'violet' };
  if (event.filled > 0 && event.filled < event.needed)
                                         return { id: 'partially_staffed',   label: 'Partially Staffed',   tone: 'amber'   };
  return { id: 'staffing_needed',        label: 'Staffing Needed',           tone: 'rose'    };
}

const STATUSES = [
  { id: 'staffing_needed',     label: 'Staffing Needed',     tone: 'rose'    },
  { id: 'reviewing',           label: 'Reviewing Applicants', tone: 'violet' },
  { id: 'partially_staffed',   label: 'Partially Staffed',   tone: 'amber'   },
  { id: 'fully_staffed',       label: 'Fully Staffed',       tone: 'emerald' },
  { id: 'ready',               label: 'Ready for Event',     tone: 'emerald' },
  { id: 'in_progress',         label: 'In Progress',         tone: 'cyan'    },
  { id: 'completed',           label: 'Completed',           tone: 'slate'   },
];

function dateRange(first, last) {
  if (!first) return '—';
  const f = parseLocalDate(first);
  if (!f) return '—';
  if (!last || last === first) return f.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const l = parseLocalDate(last);
  if (!l) return f.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const sameMonth = f.getMonth() === l.getMonth() && f.getFullYear() === l.getFullYear();
  if (sameMonth) return `${f.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${l.getDate()}`;
  return `${f.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${l.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

/* --------------------------------------------------- card subcomponent */

const ClientBadge = ({ name, logoUrl, size = 'md' }) => {
  const initials = (name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const dim = size === 'lg' ? 'w-11 h-11 text-base' : 'w-9 h-9 text-sm';
  const [errored, setErrored] = React.useState(false);
  if (logoUrl && !errored) {
    return (
      <div className={`${dim} rounded-xl bg-white border border-slate-200 grid place-items-center overflow-hidden shadow-sm shrink-0`}>
        <img
          src={logoUrl}
          alt={name || 'Client logo'}
          className="w-full h-full object-contain p-0.5"
          onError={() => setErrored(true)}
          loading="lazy"
        />
      </div>
    );
  }
  return (
    <div className={`${dim} rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-700 grid place-items-center text-white font-bold shadow-sm shrink-0`}>
      {initials || 'IV'}
    </div>
  );
};

const StatusPills = ({ event, onApplicantsClick }) => {
  const isToday = event.bucket === 'today';
  const isPast = (event.last_date || event.first_date || '') < TODAY;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {isToday && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 uppercase tracking-wide">
          ● Today
        </span>
      )}
      {event.applicants > 0 && !isPast && (
        <button
          onClick={(e) => { e.stopPropagation(); onApplicantsClick?.(event); }}
          className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 uppercase tracking-wide"
          data-testid={`pill-applicants-${event.key}`}
        >
          {event.applicants} to review
        </button>
      )}
      {!isPast && event.briefing_pct < 50 && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 uppercase tracking-wide flex items-center gap-1">
          <AlertCircle className="w-2.5 h-2.5" /> Briefing {event.briefing_pct}%
        </span>
      )}
    </div>
  );
};

const StatTile = ({ label, value, tone = 'slate', accent = '' }) => (
  <div className={`rounded-xl px-3 py-2 bg-${tone}-50 border border-${tone}-100`}>
    <div className={`text-[10px] uppercase font-bold tracking-wide text-${tone}-500`}>{label}</div>
    <div className={`text-sm font-bold leading-tight mt-0.5 ${accent || `text-${tone}-900`}`}>{value}</div>
  </div>
);

const ActionBtn = ({ icon: Icon, label, onClick, accent, testid, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex flex-col items-center justify-center gap-1 py-2 rounded-lg text-[10px] font-semibold transition
      ${disabled
        ? 'bg-slate-50 text-slate-300 border border-slate-100 cursor-not-allowed'
        : accent
        ? 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-sm'
        : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200'}`}
    data-testid={testid}
  >
    <Icon className="w-3.5 h-3.5" />
    <span className="leading-none">{label}</span>
  </button>
);

const EventCard = ({ event, intel, onOpen, onMessage, onReminder, onStaffing, onBriefing, onInvite, selectMode, selected, onToggleSelect, onDelete }) => {
  const navigate = useNavigate();
  const tone = fillTone(event.filled, event.needed);
  const fillPct = event.needed ? Math.min(100, Math.round((event.filled / event.needed) * 100)) : 0;
  const isPast = (event.last_date || event.first_date || '') < TODAY;
  const briefingTone = isPast ? 'slate' : (event.briefing_pct >= 80 ? 'emerald' : event.briefing_pct >= 50 ? 'amber' : 'rose');
  const status = eventStatus(event);

  // Iter-274 — entire card surface is now tappable (Admin reported the inner
  // action buttons were too small to hit on mobile). Inner buttons stop
  // propagation so they continue to drive their specific action.
  const stop = (fn) => (e) => { e.stopPropagation(); fn?.(event); };

  return (
    <div
      className={`group relative rounded-2xl border shadow-sm transition overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-400
        ${isPast
          ? 'bg-slate-50 border-slate-200 hover:border-slate-300 hover:shadow-md'
          : 'bg-white border-slate-200 hover:border-cyan-300 hover:shadow-lg'}`}
      data-testid={`event-card-${event.key}`}
      onClick={() => onOpen(event)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(event); } }}
      role="button"
      tabIndex={0}
      aria-label={`Open ${event.event_title} — ${event.client_name}`}
    >
      {/* Accent strip — coloured by status */}
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-${status.tone}-400 to-${status.tone}-600`} />

      {selectMode && (
        <div
          className="absolute top-2 left-2 z-20 bg-white rounded-md p-1 shadow"
          onClick={(e) => { e.stopPropagation(); }}
          data-testid={`select-event-${event.key}`}
        >
          <Checkbox checked={!!selected} onCheckedChange={() => onToggleSelect?.(event)} />
        </div>
      )}
      {onDelete && !selectMode && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(event); }}
          title="Delete permanently"
          className="absolute bottom-2 right-2 z-20 opacity-0 group-hover:opacity-100 transition p-1.5 rounded-lg bg-white/95 border border-red-200 text-red-600 hover:bg-red-50 shadow-sm"
          data-testid={`delete-event-${event.key}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}

      <div className="p-5">
        {/* Header — client + prominent status badge */}
        <div className="flex items-start gap-3 mb-3">
          <ClientBadge name={event.client_name} logoUrl={event.client_logo_url} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[11px] font-bold text-cyan-700 uppercase tracking-wider truncate">{event.client_name}</div>
              {event.series_total > 1 && (
                <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 uppercase tracking-wide shrink-0" data-testid={`event-series-badge-${event.key}`}>
                  <Layers className="w-2.5 h-2.5" /> Day {event.series_day_index} of {event.series_total}
                </span>
              )}
              {event.is_multi_day && !(event.series_total > 1) && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase tracking-wide shrink-0">
                  {event.total_shifts}-Day
                </span>
              )}
            </div>
            <h3 className="text-lg font-bold text-slate-900 leading-tight mt-0.5 truncate" data-testid={`event-title-${event.key}`}>
              {event.event_title}
            </h3>
            <div className="flex items-center gap-1 text-xs text-slate-500 mt-1 min-w-0">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">
                {[event.venue, event.city, event.state].filter(Boolean).join(', ') || '—'}
              </span>
            </div>
            {event.series_total > 1 && event.series_dates && (
              <div className="flex items-center gap-1 flex-wrap mt-1.5" data-testid={`event-series-dates-${event.key}`}>
                <Layers className="w-3 h-3 text-indigo-400 shrink-0" />
                {event.series_dates.map((d) => (
                  <span
                    key={d.shift_id}
                    className={d.is_current
                      ? 'text-[10px] font-semibold bg-indigo-600 text-white rounded px-1.5 py-0.5'
                      : 'text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5'}
                  >
                    {fmtSeriesDate(d.shift_date)}
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* Prominent status badge */}
          <div
            className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-${status.tone}-50 border border-${status.tone}-200 text-${status.tone}-700`}
            data-testid={`event-status-${event.key}`}
            title={`Event status: ${status.label}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full bg-${status.tone}-500`} />
            <span className="text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">{status.label}</span>
          </div>
        </div>

        {/* Secondary pill row — supporting context */}
        <div className="mb-4">
          <StatusPills event={event} onApplicantsClick={onStaffing} />
        </div>

        {/* Iter-401 (Phase 1 AI Ops) — self-explaining intelligence panel */}
        {intel && (
          <StaffingIntelligencePanel
            intel={intel}
            onAction={async (a) => {
              // Iter-402 Track C — one-tap actions wired to real endpoints
              const shiftId = event.shifts?.[0]?.id;
              try {
                if (a.id === 'invite_backup') {
                  // Backups need admin control (pick specific people or widen
                  // the market/radius) and must be tagged as backup — so open
                  // the full invite tools in BACKUP mode instead of blasting.
                  onInvite?.({ event, asBackup: true });
                  return;
                }
                if (a.id === 'invite_more' && shiftId) {
                  const r = await api.post(`/admin/ai-ops/shifts/${shiftId}/invite-more`);
                  const invited = r.data?.invited || 0;
                  if (invited > 0) {
                    toast.success(r.data.message || 'Invites dispatched');
                  } else {
                    // No local match — don't dead-end. Open the invite tools so
                    // the admin can widen the radius/market or pick specific people.
                    toast.info('No new local talent — opening invite tools to widen your search.');
                    onInvite?.({ event, asBackup: false });
                  }
                  return;
                }
                if (a.id === 'promote_backup' && shiftId) {
                  const r = await api.post(`/admin/ai-ops/shifts/${shiftId}/promote-backup`);
                  toast.success(r.data.message || 'Backup promoted');
                  return;
                }
                if (a.id === 'send_reminder' && shiftId) {
                  const r = await api.post(`/admin/ai-ops/shifts/${shiftId}/send-reminders`);
                  toast.success(r.data.message || 'Reminders sent');
                  return;
                }
                if (a.id === 'request_client_briefing' && shiftId) {
                  const r = await api.post(`/admin/briefing/${shiftId}/request-completion`);
                  if (r.data.skipped) {
                    toast.info(
                      r.data.reason === 'recently_requested'
                        ? 'Already asked the client within the last 12h.'
                        : (r.data.reason || 'Nothing to request.'),
                    );
                  } else {
                    toast.success(r.data.message || 'Client notified');
                  }
                  return;
                }
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Action failed');
                return;
              }
              // Fallback / navigation-style actions
              if (a.id === 'review_applicants') onStaffing?.(event);
              else if (a.id === 'complete_briefing') onBriefing?.(event);
              else onOpen?.(event);
            }}
          />
        )}

        {/* Role + Rate band */}
        <div className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2.5 mb-3 border border-slate-100">
          <div className="min-w-0 flex-1 mr-3">
            <div className="text-[10px] uppercase font-bold tracking-wide text-slate-400">
              {event.roles_count > 1 ? `${event.roles_count} Roles` : 'Role'}
            </div>
            <div className="text-sm font-bold text-slate-900 truncate" title={(event.role_names || []).join(', ')}>
              {event.roles_count > 1
                ? (event.role_names || []).join(' · ')
                : (event.role_name || '—')}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase font-bold tracking-wide text-slate-400">Pay</div>
            <div className="text-base font-bold text-cyan-700">{event.hourly_rate ? `$${event.hourly_rate}/hr` : '—'}</div>
          </div>
        </div>

        {/* Stat grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <StatTile
            label="Dates"
            value={dateRange(event.first_date, event.last_date)}
            tone="slate"
          />
          <StatTile
            label="Shifts"
            value={`${event.total_shifts} ${event.is_multi_day ? 'days' : 'day'}`}
            tone="slate"
          />
          <div className={`rounded-xl px-3 py-2 bg-${tone}-50 border border-${tone}-100`}>
            <div className={`text-[10px] uppercase font-bold tracking-wide text-${tone}-600`}>Fill Rate</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-sm font-bold text-${tone}-700`}>{event.filled}/{event.needed || '?'}</span>
              <div className="flex-1 h-1.5 bg-white/60 rounded-full overflow-hidden">
                <div className={`h-full bg-${tone}-500`} style={{ width: `${fillPct}%` }} />
              </div>
            </div>
          </div>
          <div className={`rounded-xl px-3 py-2 bg-${briefingTone}-50 border border-${briefingTone}-100`}>
            <div className={`text-[10px] uppercase font-bold tracking-wide text-${briefingTone}-600`}>Briefing</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-sm font-bold text-${briefingTone}-700`}>{event.briefing_pct}%</span>
              <div className="flex-1 h-1.5 bg-white/60 rounded-full overflow-hidden">
                <div className={`h-full bg-${briefingTone}-500`} style={{ width: `${event.briefing_pct}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* Applicants alert row */}
        {event.applicants > 0 && !isPast && (
          <button
            onClick={stop(onStaffing)}
            className="w-full mb-3 px-3 py-2 rounded-xl bg-violet-50 hover:bg-violet-100 border border-violet-200 text-violet-800 flex items-center justify-between transition"
            data-testid={`applicants-cta-${event.key}`}
          >
            <span className="text-xs font-semibold flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> {event.applicants} new applicant{event.applicants !== 1 ? 's' : ''} to review
            </span>
            <span className="text-xs font-bold">→</span>
          </button>
        )}

        {/* Action row */}
        <div className="grid grid-cols-6 gap-1.5">
          <ActionBtn icon={Eye}            label="View"      onClick={stop(onOpen)}       accent  testid={`act-view-${event.key}`} />
          <ActionBtn icon={Users}          label="Staffing"  onClick={stop(onStaffing)}           testid={`act-staffing-${event.key}`} />
          <ActionBtn icon={ClipboardCheck} label="Briefing"  onClick={stop(onBriefing)}           testid={`act-briefing-${event.key}`} />
          <ActionBtn icon={MessageSquare}  label="Message"   onClick={stop(onMessage)}            testid={`act-message-${event.key}`} />
          {isPast ? (
            <ActionBtn icon={History}      label="Records"
              onClick={stop(() => navigate(`/admin/event-history?event=${event.primary_shift_id}`))}
              disabled={!event.primary_shift_id}
              testid={`act-records-${event.key}`} />
          ) : (
            <ActionBtn icon={Send}         label="Remind"    onClick={stop(onReminder)}           testid={`act-remind-${event.key}`} />
          )}
          {/* Iter-290b / Iter-313g — Share Job button. `variant='action'`
              matches the sibling ActionBtn style (icon + label) so the
              6-column action row reads consistently and the "Share"
              label is visible next to Message + Remind. */}
          <div onClick={(e) => e.stopPropagation()}>
            <ShareJobButton
              variant="action"
              testIdSuffix={event.key}
              job={{
                id: event.key,
                title: event.event_title,
                company: event.client_name,
                location: [event.venue, event.city, event.state].filter(Boolean).join(', ') || null,
                dateRange: dateRange(event.first_date, event.last_date),
                payRate: event.hourly_rate,
                roleName: event.role_name,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------- grouped client section */

const ClientSection = ({ group, intelById, collapsed, onToggle, onOpen, onMessage, onReminder, onStaffing, onBriefing, onInvite, onDelete }) => {
  const shortBy = Math.max(0, group.total_needed - group.total_filled);
  return (
    <section className="space-y-3" data-testid={`client-section-${group.client_name}`}>
      <header className="flex items-center gap-3 px-1">
        <ClientBadge name={group.client_name} logoUrl={group.client_logo_url} />
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-slate-900 truncate">{group.client_name}</h2>
          <p className="text-xs text-slate-500">
            {group.total_events} event{group.total_events !== 1 ? 's' : ''} · {group.total_shifts} shifts ·{' '}
            <span className={shortBy ? 'text-amber-700 font-semibold' : 'text-emerald-700 font-semibold'}>
              {group.total_filled}/{group.total_needed} staffed
            </span>
            {group.total_applicants > 0 && <span className="text-violet-700 font-semibold"> · {group.total_applicants} to review</span>}
          </p>
        </div>
        <button onClick={onToggle} className="text-xs text-slate-500 hover:text-slate-900 p-1.5 rounded-lg hover:bg-slate-100">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </header>
      {!collapsed && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {group.items.map(e => (
            <EventCard
              key={e.key}
              event={e}
              intel={intelById?.[e.key]}
              onOpen={onOpen}
              onMessage={onMessage}
              onReminder={onReminder}
              onStaffing={onStaffing}
              onBriefing={onBriefing}
              onInvite={onInvite}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </section>
  );
};

/* ----------------------------- needs-attention rail ------------------ */

const AttentionTile = ({ icon: Icon, tone, count, label, hint, onClick, testid }) => (
  <button
    onClick={onClick}
    className={`text-left bg-white border border-${tone}-200 hover:border-${tone}-400 hover:shadow-md rounded-2xl p-3 transition group`}
    data-testid={testid}
  >
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 rounded-xl bg-${tone}-100 text-${tone}-700 grid place-items-center shrink-0`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className={`text-[10px] uppercase tracking-wider font-bold text-${tone}-600`}>{label}</div>
        <div className="flex items-baseline gap-1">
          <span className="text-xl font-extrabold text-slate-900">{count}</span>
          <span className="text-[10px] text-slate-500 truncate">{hint}</span>
        </div>
      </div>
    </div>
  </button>
);

const PriorityMiniCard = ({ event, onOpen }) => {
  const status = eventStatus(event);
  const reasons = [];
  if (event.needed > event.filled) reasons.push(`Needs ${event.needed - event.filled}`);
  if (event.applicants > 0) reasons.push(`${event.applicants} apps`);
  if (event.briefing_pct < 50) reasons.push(`Briefing ${event.briefing_pct}%`);
  const f = parseLocalDate(event.first_date);
  const day = f ? f.toLocaleDateString('en-US', { weekday: 'short' }) : '';
  const date = f ? f.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  return (
    <button
      onClick={() => onOpen(event)}
      className="snap-start shrink-0 w-72 text-left bg-white border border-slate-200 hover:border-cyan-300 hover:shadow-md rounded-2xl p-3 transition"
      data-testid={`attn-card-${event.key}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-${status.tone}-50 text-${status.tone}-700 border border-${status.tone}-200 uppercase tracking-wide`}>
          {status.label}
        </span>
        <span className="text-[10px] font-semibold text-slate-500 ml-auto">{day} · {date}</span>
      </div>
      <div className="text-[11px] font-bold text-cyan-700 uppercase tracking-wide truncate">{event.client_name}</div>
      <div className="text-sm font-bold text-slate-900 truncate">{event.event_title}</div>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {reasons.map(r => (
          <span key={r} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            {r}
          </span>
        ))}
      </div>
    </button>
  );
};

const NeedsAttentionRail = ({ attention, onJump, onOpen }) => {
  if (!attention.priority.length && !attention.unfilled.length && !attention.applicantsPending.length && !attention.missingBriefing.length && !attention.startingSoon.length) {
    return null;
  }
  return (
    <div className="mt-5" data-testid="needs-attention-rail">
      <div className="flex items-center gap-2 mb-2 px-1">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">Needs Attention</h2>
        <span className="text-xs text-slate-400">— auto-surfaced operational items</span>
      </div>

      {/* 4 clickable summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <AttentionTile icon={AlertCircle}    tone="rose"    count={attention.unfilled.length}          label="Unfilled"      hint="slots open"            onClick={() => onJump('unfilled')}     testid="attn-tile-unfilled" />
        <AttentionTile icon={Users}          tone="violet"  count={attention.applicantsPending.length} label="Applicants"    hint="awaiting review"       onClick={() => onJump('applicants')}   testid="attn-tile-applicants" />
        <AttentionTile icon={ClipboardCheck} tone="amber"   count={attention.missingBriefing.length}   label="Briefings"     hint="missing or incomplete" onClick={() => onJump('briefing')}     testid="attn-tile-briefing" />
        <AttentionTile icon={Send}           tone="cyan"    count={attention.startingSoon.length}      label="Starting Soon" hint="within 48 hours"       onClick={() => onJump('startingSoon')} testid="attn-tile-soon" />
      </div>

      {/* Horizontal priority list */}
      {attention.priority.length > 0 && (
        <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1" data-testid="attn-priority-rail">
          {attention.priority.map(e => (
            <PriorityMiniCard key={e.key} event={e} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------- page --- */

const AdminJobsV2 = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadDiag, setLoadDiag] = useState(null);
  const [reloadCounter, setReloadCounter] = useState(0);
  // 2026-09 — Filter persistence is OPT-IN. By default the Jobs page ALWAYS
  // opens showing every relevant job; a stale filter can no longer silently
  // hide jobs across visits. Filters persist between visits ONLY when the
  // admin turns on "Remember filters".
  const REMEMBER_KEY = 'jobsv2_remember_filters';
  const SAVED_KEY = 'jobsv2_saved_filters';
  const _rememberInit = (() => { try { return localStorage.getItem(REMEMBER_KEY) === '1'; } catch { return false; } })();
  const _savedInit = (() => {
    try { return _rememberInit ? (JSON.parse(localStorage.getItem(SAVED_KEY) || '{}') || {}) : {}; }
    catch { return {}; }
  })();
  const [rememberFilters, setRememberFilters] = useState(_rememberInit);
  const [bucket, setBucket] = useState(_savedInit.bucket || 'all');
  const [view, setView] = useState('cards');   // cards | calendar | list
  const [groupBy, setGroupBy] = useState('none'); // none (By Date — default) | client
  const [statusFilter, setStatusFilter] = useState(_savedInit.statusFilter || 'all'); // matches eventStatus().id or 'all'
  const [attentionFilter, setAttentionFilter] = useState(_savedInit.attentionFilter || null);
  const [aiCategoryFilter, setAiCategoryFilter] = useState(_savedInit.aiCategoryFilter || null);
  const [query, setQuery] = useState(_savedInit.query || '');
  const [collapsedClients, setCollapsedClients] = useState(new Set());
  const [drawerEvent, setDrawerEvent] = useState(null);
  const [drawerTab, setDrawerTab] = useState('shifts');
  // 2026-09 — Invite Talent modal (opened from a card's Invite/Backup action).
  const [inviteModal, setInviteModal] = useState(null);
  const openInvite = ({ event, asBackup }) => {
    const shift = event.shifts?.[0];
    if (!shift?.id) { toast.error('No shift found for this event'); return; }
    setInviteModal({
      shiftId: shift.id,
      asBackup: !!asBackup,
      shiftMeta: {
        role_name: event.role_name,
        venue: event.venue,
        city: event.city,
        state: event.state,
        shift_date: shift.shift_date,
      },
    });
  };

  // 2026-09 — A refresh used to re-apply the URL's bucket/attention/ai_category
  // filters (e.g. arriving from a "Today" or "Staffing Needed" home tile), which
  // made the whole jobs list look empty ("136 shifts loaded but none match").
  // A single Clear-All-Filters action resets every filter AND wipes the URL
  // params so the default All-Jobs view reliably shows everything.
  const hasActiveFilters = bucket !== 'all' || statusFilter !== 'all'
    || !!attentionFilter || !!aiCategoryFilter || !!query.trim();
  const clearAllFilters = () => {
    setBucket('all');
    setStatusFilter('all');
    setAttentionFilter(null);
    setAiCategoryFilter(null);
    setQuery('');
    setSearchParams({}, { replace: true });
    try { localStorage.removeItem(SAVED_KEY); } catch { /* ignore */ }
  };

  // On mount, when NOT remembering filters, strip any leftover URL filter
  // params so a refresh/return always lands on the full All-Jobs view.
  useEffect(() => {
    if (!rememberFilters && (searchParams.get('bucket') || searchParams.get('attention') || searchParams.get('ai_category'))) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist (or forget) filters based on the opt-in toggle.
  useEffect(() => {
    try {
      if (rememberFilters) {
        localStorage.setItem(REMEMBER_KEY, '1');
        localStorage.setItem(SAVED_KEY, JSON.stringify({
          bucket, statusFilter, attentionFilter, aiCategoryFilter, query,
        }));
      } else {
        localStorage.setItem(REMEMBER_KEY, '0');
        localStorage.removeItem(SAVED_KEY);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rememberFilters, bucket, statusFilter, attentionFilter, aiCategoryFilter, query]);

  useEffect(() => {
    let m = true;
    setLoading(true);
    setLoadError(null);
    api.get('/admin/shifts?per_page=500&sort=upcoming')
      .then(r => {
        if (!m) return;
        const list = r.data?.shifts || r.data?.items || (Array.isArray(r.data) ? r.data : []);
        setShifts(list);
        // Iter-229h — capture diagnostic context whenever we get a 200 but
        // zero shifts; this is the symptom the user saw and we need ground
        // truth from the actual session for support tickets.
        if (list.length === 0) {
          setLoadDiag({
            http: r.status,
            total: r.data?.total ?? 'n/a',
            keys: r.data ? Object.keys(r.data) : [],
            preview: JSON.stringify(r.data).slice(0, 300),
            at: new Date().toISOString(),
            build_version: process.env.REACT_APP_BUILD_VERSION || 'unknown',
            build_time: process.env.REACT_APP_BUILD_TIME || 'unknown',
            sw_version: (typeof window !== 'undefined' && window.__SW_VERSION__) || 'unknown',
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 120) : 'unknown',
            token_present: !!localStorage.getItem('token'),
            user_email: (JSON.parse(localStorage.getItem('user') || '{}') || {}).email || 'unknown',
          });
        } else {
          setLoadDiag(null);
        }
      })
      .catch(err => {
        if (!m) return;
        console.error('Failed to load shifts', err);
        setLoadError({
          status: err?.response?.status,
          message: err?.response?.data?.detail || err?.message || 'Network error',
          at: new Date().toISOString(),
        });
      })
      .finally(() => { if (m) setLoading(false); });
    return () => { m = false; };
  }, [reloadCounter]);

  const allEvents = useMemo(() => groupShifts(shifts), [shifts]);

  // Iter-403 · Count-mismatch fix — compute the "base" scope (bucket +
  // attention + query, but NOT the status pill). The status chip counts and
  // the displayed list BOTH derive from this, so a chip count is exactly the
  // number of shifts you'll see after clicking it (never "28" then zero).
  const baseScope = useMemo(() => {
    let f = allEvents;
    if (bucket !== 'all') f = f.filter(e => e.bucket === bucket);
    if (attentionFilter) {
      const now = new Date();
      const future = (e) => (e.first_date || '') >= TODAY;
      if (attentionFilter === 'unfilled')    f = f.filter(e => future(e) && e.needed > 0 && e.filled < e.needed);
      if (attentionFilter === 'applicants')  f = f.filter(e => future(e) && e.applicants > 0);
      if (attentionFilter === 'briefing')    f = f.filter(e => future(e) && e.briefing_pct < 50);
      if (attentionFilter === 'startingSoon')f = f.filter(e => {
        const sd = parseLocalDate(e.first_date);
        return sd && (sd - now) > 0 && (sd - now) <= 48 * 3600 * 1000;
      });
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      f = f.filter(e =>
        (e.event_title || '').toLowerCase().includes(q) ||
        (e.client_name || '').toLowerCase().includes(q) ||
        (e.role_name || '').toLowerCase().includes(q) ||
        (e.city || '').toLowerCase().includes(q)
      );
    }
    return f;
  }, [allEvents, bucket, attentionFilter, query]);

  // Status-pill counts — computed over baseScope so every chip count matches
  // exactly what the list shows when that status is selected.
  const statusCounts = useMemo(() => {
    const c = {};
    for (const e of baseScope) {
      const id = eventStatus(e).id;
      c[id] = (c[id] || 0) + 1;
    }
    return c;
  }, [baseScope]);

  const preCategoryScope = useMemo(() => {
    if (statusFilter === 'all') return baseScope;
    return baseScope.filter(e => eventStatus(e).id === statusFilter);
  }, [baseScope, statusFilter]);

  // Iter-401 (Phase 1 AI Ops) — classify events in the current scope.
  // Counts here EXACTLY match what clicking a chip will show.
  const aiSummary = useMemo(() => {
    const synthetic = preCategoryScope.map((e) => ({
      id:                   e.key,
      talent_needed:        e.needed,
      confirmed_count:      e.filled,
      staffed_count:        e.filled,
      applicant_count:      e.applicants,
      backups_needed:       (e.shifts || []).reduce((n, s) => n + Number(s.backups_needed || 0), 0),
      backup_count:         (e.shifts || []).reduce((n, s) => n + Number(s.backup_count || 0), 0),
      primary_count:        (e.shifts || []).reduce((n, s) => n + Number(s.primary_count || 0), 0),
      day_confirm_missed_count: (e.shifts || []).reduce((n, s) => n + Number(s.day_confirm_missed_count || 0), 0),
      shift_date:           e.first_date,
      start_time:           (e.shifts || [])[0]?.start_time,
      status:               (e.shifts || [])[0]?.status,
      bucket:                undefined,
      role_name:            e.role_name,
      briefing_pct:         e.briefing_pct,
      urgent_replacement_active: (e.shifts || []).some(s => s.urgent_replacement_active),
    }));
    return summarizeStaffing(synthetic, { role: 'admin' });
  }, [preCategoryScope]);

  const events = useMemo(() => {
    if (!aiCategoryFilter) return preCategoryScope;
    return preCategoryScope.filter(e => aiSummary.intelligenceById[e.key]?.category === aiCategoryFilter);
  }, [preCategoryScope, aiCategoryFilter, aiSummary]);

  const clientGroups = useMemo(() => groupByClient(events), [events]);

  // ---- Permanent delete (single + bulk) with assigned-talent warning ----
  const [selectMode, setSelectMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [deleteDlg, setDeleteDlg] = useState({
    open: false, ids: [], label: '', impact: null, confirmText: '', reason: '', loading: false,
  });
  const shiftIdsForEvent = (e) => (e.shifts || []).map((s) => s.id).filter(Boolean);
  const toggleSelectEvent = (e) =>
    setSelectedKeys((p) => (p.includes(e.key) ? p.filter((k) => k !== e.key) : [...p, e.key]));
  const allEventsSelected = events.length > 0 && events.every((e) => selectedKeys.includes(e.key));
  const toggleSelectAllEvents = () =>
    setSelectedKeys(allEventsSelected ? [] : events.map((e) => e.key));
  const exitSelect = () => { setSelectMode(false); setSelectedKeys([]); };

  const openDeleteDialog = async (ids, label) => {
    setDeleteDlg({ open: true, ids, label: label || '', impact: null, confirmText: '', reason: '', loading: true });
    try {
      const res = await api.post('/admin/shifts/bulk-delete-impact', { shift_ids: ids });
      setDeleteDlg((d) => ({ ...d, impact: res.data, loading: false }));
    } catch {
      setDeleteDlg((d) => ({ ...d, impact: { total_assigned_talent: 0, blocked_count: 0 }, loading: false }));
    }
  };
  const handleDeleteEvent = (e) => openDeleteDialog(shiftIdsForEvent(e), e.event_title || 'this job');
  const openBulkDelete = () => {
    const ids = events.filter((e) => selectedKeys.includes(e.key)).flatMap(shiftIdsForEvent);
    openDeleteDialog(ids, `${selectedKeys.length} job(s)`);
  };
  const confirmDeleteNow = async () => {
    const { ids, reason } = deleteDlg;
    if (!ids.length) return;
    setDeleteDlg((d) => ({ ...d, loading: true }));
    try {
      const res = await api.post('/admin/shifts/bulk-delete', {
        shift_ids: ids, confirmation: 'DELETE', reason: reason || null,
      });
      const s = res.data?.summary || {};
      toast.success(`Deleted ${s.deleted || 0} · ${s.notified_talent || 0} talent notified`);
      if (s.blocked) toast.warning(`${s.blocked} skipped (worked/settlement history) — archive those instead.`);
      setDeleteDlg({ open: false, ids: [], label: '', impact: null, confirmText: '', reason: '', loading: false });
      exitSelect();
      setReloadCounter((n) => n + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
      setDeleteDlg((d) => ({ ...d, loading: false }));
    }
  };
  const counts = useMemo(() => {
    const c = { today: 0, tomorrow: 0, this_week: 0, next_week: 0, future: 0, all: allEvents.length };
    for (const e of allEvents) c[e.bucket] = (c[e.bucket] || 0) + 1;
    return c;
  }, [allEvents]);

  // Top-level hero stats
  const heroStats = useMemo(() => {
    const todayEvents = allEvents.filter(e => e.bucket === 'today').length;
    const thisWeek = allEvents.filter(e => ['today', 'tomorrow', 'this_week'].includes(e.bucket)).length;
    const applicants = allEvents.reduce((n, e) => n + e.applicants, 0);
    const unfilled = allEvents.reduce((n, e) => n + Math.max(0, e.needed - e.filled), 0);
    return { todayEvents, thisWeek, applicants, unfilled };
  }, [allEvents]);

  // Needs-Attention bucketing: auto-surfaces urgent operational items
  const attention = useMemo(() => {
    const now = new Date();
    const in48h = (e) => {
      const sd = parseLocalDate(e.first_date);
      return sd && (sd - now) > 0 && (sd - now) <= 48 * 3600 * 1000;
    };
    const future = (e) => (e.first_date || '') >= TODAY;
    const unfilled = allEvents.filter(e => future(e) && e.needed > 0 && e.filled < e.needed);
    const applicantsPending = allEvents.filter(e => future(e) && e.applicants > 0);
    const missingBriefing = allEvents.filter(e => future(e) && e.briefing_pct < 50);
    const startingSoon = allEvents.filter(in48h);
    // Build a deduped priority list, most urgent first
    const priorityKeys = new Set();
    const priority = [];
    const addAll = (arr) => {
      for (const e of arr) if (!priorityKeys.has(e.key)) { priorityKeys.add(e.key); priority.push(e); }
    };
    addAll(startingSoon);
    addAll(unfilled.filter(e => e.bucket === 'today' || e.bucket === 'tomorrow'));
    addAll(applicantsPending);
    addAll(unfilled);
    addAll(missingBriefing);
    return {
      unfilled,
      applicantsPending,
      missingBriefing,
      startingSoon,
      priority: priority.slice(0, 8),
    };
  }, [allEvents]);

  const toggleClient = (name) => setCollapsedClients(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const openDrawer = (event, tab = 'shifts') => { setDrawerEvent(event); setDrawerTab(tab); };
  const onMessage  = (event) => navigate(`/messages?client_id=${event.client_id || ''}&event=${encodeURIComponent(event.event_title)}`);
  const onReminder = (event) => { toast.success(`Pre-shift reminder queued for ${event.event_title}`, { description: `${event.filled} staffed talent will be notified.` }); };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-16" data-testid="admin-jobs-v2-page">
      {/* Hero header */}
      <div className="bg-white border-b border-slate-200 px-4 md:px-8 py-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start md:items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">All Jobs</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {allEvents.length} events · {shifts.length} shifts ·{' '}
                <button onClick={() => navigate('/admin/shifts?classic=1')} className="text-cyan-700 hover:underline font-semibold" data-testid="switch-to-legacy-admin">
                  Switch to classic table
                </button>
              </p>
            </div>
            <Button onClick={() => navigate('/admin/shifts/create')} className="bg-cyan-600 hover:bg-cyan-700 h-10 px-4" data-testid="post-job-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Post a Job
            </Button>
          </div>

          {/* Hero header */}
        </div>
      </div>

      <div className="px-4 md:px-8 pt-4 md:pt-5">
        <div className="max-w-7xl mx-auto space-y-4">
          {/* Iter-403 · AI Priority Briefing — replaces the old hero
              stat tiles + Needs Attention rail. One clear command-center
              panel with plain-English recommendations. Every count MATCHES
              the number of shifts you'll see after clicking. */}
          <AiPriorityBriefing
            countsByCategory={aiSummary.countsByCategory}
            totals={aiSummary.totals}
            onOpenBriefingCommand={() => navigate('/admin/briefing-command')}
            onSelectCategory={(cat) => {
              setAiCategoryFilter(cat);
              setAttentionFilter(null);
              setStatusFilter('all');
              const n = new URLSearchParams(searchParams);
              if (cat) n.set('ai_category', cat); else n.delete('ai_category');
              n.delete('attention');
              setSearchParams(n, { replace: true });
            }}
          />

          {/* Compact category filter chips (still supported for the
              users that prefer the taxonomy view). */}
          <div>
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Filter by category</span>
              <span className="text-xs text-slate-400">— tap to narrow the list</span>
            </div>
            <AiCategoryChipStrip
              countsByCategory={aiSummary.countsByCategory}
              active={aiCategoryFilter}
              onSelect={(cat) => {
                setAiCategoryFilter(cat);
                setAttentionFilter(null);
                setStatusFilter('all');
                const n = new URLSearchParams(searchParams);
                if (cat) n.set('ai_category', cat); else n.delete('ai_category');
                n.delete('attention');
                setSearchParams(n, { replace: true });
              }}
            />
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-5">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/40">
            {/* View toggle */}
            <div className="bg-slate-100 rounded-xl p-1 flex text-xs font-semibold">
              <button onClick={() => setView('cards')}    className={`px-3 py-1.5 rounded-lg flex items-center gap-1 transition ${view === 'cards' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`} data-testid="view-cards"><LayoutGrid className="w-3.5 h-3.5" /> Cards</button>
              <button onClick={() => setView('calendar')} className={`px-3 py-1.5 rounded-lg flex items-center gap-1 transition ${view === 'calendar' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`} data-testid="view-calendar"><CalIcon className="w-3.5 h-3.5" /> Calendar</button>
              <button onClick={() => setView('list')}     className={`px-3 py-1.5 rounded-lg flex items-center gap-1 transition ${view === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`} data-testid="view-list"><ListIcon className="w-3.5 h-3.5" /> List</button>
            </div>

            {/* Bucket pills */}
            <div className="flex items-center gap-1 ml-1 text-xs flex-wrap">
              {BUCKETS.map(b => (
                <button
                  key={b.id}
                  onClick={() => setBucket(b.id)}
                  className={`px-3 py-1.5 rounded-full font-semibold transition border ${
                    bucket === b.id
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                  }`}
                  data-testid={`bucket-${b.id}`}
                >
                  {b.label}
                  <span className={`ml-1.5 text-[10px] font-bold ${bucket === b.id ? 'text-white/80' : 'text-slate-400'}`}>
                    {counts[b.id] ?? 0}
                  </span>
                </button>
              ))}
            </div>

            <div className="flex-1" />

            {/* Group toggle (cards view only) */}
            {view === 'cards' && (
              <div className="bg-slate-100 rounded-xl p-1 flex text-xs font-semibold">
                <button onClick={() => setGroupBy('client')} className={`px-3 py-1.5 rounded-lg ${groupBy === 'client' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600'}`} data-testid="group-client">By Client</button>
                <button onClick={() => setGroupBy('none')}   className={`px-3 py-1.5 rounded-lg ${groupBy === 'none' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600'}`} data-testid="group-none">By Date</button>
              </div>
            )}

            <Input
              type="search"
              placeholder="Search client / event / role / city…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="text-xs h-9 w-56 md:w-64"
              data-testid="search-input"
            />
            <Button variant="outline" size="sm" className="text-xs h-9" data-testid="filters-btn">
              <Filter className="w-3.5 h-3.5 mr-1" /> Filters
            </Button>
            <button
              type="button"
              onClick={() => setRememberFilters(v => !v)}
              title={rememberFilters
                ? 'Filters are remembered between visits. Click to always open showing all jobs.'
                : 'Jobs always open showing all jobs. Click to remember your filters between visits.'}
              className={`text-[11px] font-semibold h-9 px-3 rounded-lg border transition flex items-center gap-1.5 shrink-0
                ${rememberFilters
                  ? 'bg-cyan-600 text-white border-cyan-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
              data-testid="remember-filters-toggle"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${rememberFilters ? 'bg-white' : 'bg-slate-300'}`} />
              {rememberFilters ? 'Remembering filters' : 'Remember filters'}
            </button>
          </div>

          {/* Status filter row */}
          <div className="px-4 py-2.5 border-b border-slate-100 bg-white flex items-center gap-2 overflow-x-auto" data-testid="status-filter-row">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 shrink-0">Status</span>
            <button
              onClick={() => setStatusFilter('all')}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition shrink-0
                ${statusFilter === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
              data-testid="status-all"
            >
              All
            </button>
            {STATUSES.map(s => {
              const c = statusCounts[s.id] || 0;
              if (c === 0 && statusFilter !== s.id) return null;
              const active = statusFilter === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setStatusFilter(active ? 'all' : s.id)}
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition shrink-0 flex items-center gap-1.5
                    ${active
                      ? `bg-${s.tone}-600 text-white border-${s.tone}-600`
                      : `bg-${s.tone}-50 text-${s.tone}-700 border-${s.tone}-200 hover:border-${s.tone}-400`}`}
                  data-testid={`status-${s.id}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white/80' : `bg-${s.tone}-500`}`} />
                  {s.label}
                  <span className={`text-[10px] font-bold ${active ? 'text-white/80' : `text-${s.tone}-500`}`}>{c}</span>
                </button>
              );
            })}
          </div>

          {/* Active filter banner */}
          {hasActiveFilters && (
            <div className="px-4 py-2 bg-slate-50/60 border-b border-slate-100 flex items-center gap-2 text-xs flex-wrap" data-testid="active-filter-banner">
              <span className="text-slate-500">Filtering:</span>
              {bucket !== 'all' && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 text-[11px] font-semibold">
                  Time: {(BUCKETS.find(b => b.id === bucket)?.label) || bucket}
                  <button onClick={() => setBucket('all')} className="hover:opacity-70" data-testid="clear-bucket-chip">×</button>
                </span>
              )}
              {attentionFilter && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-[11px] font-semibold">
                  Attention: {attentionFilter}
                  <button onClick={() => setAttentionFilter(null)} className="hover:opacity-70">×</button>
                </span>
              )}
              {statusFilter !== 'all' && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 text-[11px] font-semibold">
                  Status: {STATUSES.find(s => s.id === statusFilter)?.label}
                  <button onClick={() => setStatusFilter('all')} className="hover:opacity-70">×</button>
                </span>
              )}
              {aiCategoryFilter && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 text-[11px] font-semibold">
                  AI: {aiCategoryFilter}
                  <button onClick={() => setAiCategoryFilter(null)} className="hover:opacity-70">×</button>
                </span>
              )}
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-slate-800 text-white text-[11px] font-bold hover:bg-slate-900"
                data-testid="clear-all-filters-btn"
              >
                Clear All Filters
              </button>
              <span className="ml-auto text-slate-500 font-semibold">{events.length} event{events.length !== 1 ? 's' : ''} match</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="mt-5">
          {loading ? (
            <div className="p-16 flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-cyan-600" /></div>
          ) : view === 'cards' ? (
            events.length === 0 ? (
              <EmptyState
                diag={loadDiag}
                error={loadError}
                onRetry={() => setReloadCounter(n => n + 1)}
                totalShiftsLoaded={shifts.length}
                activeFilters={hasActiveFilters}
                onClearFilters={clearAllFilters}
              />
            ) : groupBy === 'client' ? (
              <div className="space-y-8">
                {clientGroups.map(g => (
                  <ClientSection
                    key={g.client_name}
                    group={g}
                    intelById={aiSummary.intelligenceById}
                    collapsed={collapsedClients.has(g.client_name)}
                    onToggle={() => toggleClient(g.client_name)}
                    onOpen={(e) => openDrawer(e, 'shifts')}
                    onStaffing={(e) => openDrawer(e, 'staffing')}
                    onBriefing={(e) => openDrawer(e, 'briefing')}
                    onMessage={onMessage}
                    onReminder={onReminder}
                    onInvite={openInvite}
                    onDelete={handleDeleteEvent}
                  />
                ))}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 mb-3" data-testid="jobs-bulk-toolbar">
                  <Button
                    variant={selectMode ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
                    data-testid="toggle-select-mode-btn"
                  >
                    <CheckSquare className="w-4 h-4 mr-1" />
                    {selectMode ? 'Done selecting' : 'Select to delete'}
                  </Button>
                  {selectMode && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{selectedKeys.length} selected</span>
                      <Button variant="ghost" size="sm" onClick={toggleSelectAllEvents} data-testid="select-all-btn">
                        {allEventsSelected ? 'Clear all' : 'Select all'}
                      </Button>
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        disabled={selectedKeys.length === 0}
                        onClick={openBulkDelete}
                        data-testid="bulk-delete-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Delete {selectedKeys.length || ''}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {events.map(e => (
                    <EventCard
                      key={e.key}
                      event={e}
                      intel={aiSummary.intelligenceById[e.key]}
                      onOpen={(ev) => openDrawer(ev, 'shifts')}
                      onStaffing={(ev) => openDrawer(ev, 'staffing')}
                      onBriefing={(ev) => openDrawer(ev, 'briefing')}
                      onMessage={onMessage}
                      onReminder={onReminder}
                      onInvite={openInvite}
                      onDelete={handleDeleteEvent}
                      selectMode={selectMode}
                      selected={selectedKeys.includes(e.key)}
                      onToggleSelect={toggleSelectEvent}
                    />
                  ))}
                </div>
              </>
            )
          ) : view === 'calendar' ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <CalendarView events={events} onOpen={(e) => openDrawer(e, 'shifts')} />
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <ListView
                clientGroups={clientGroups}
                events={events}
                groupBy={groupBy}
                onOpen={(e) => openDrawer(e, 'shifts')}
                onRetry={() => setReloadCounter(n => n + 1)}
              />
            </div>
          )}
        </div>
      </div>

      <EventDetailDrawer
        event={drawerEvent}
        initialTab={drawerTab}
        onClose={() => setDrawerEvent(null)}
        onOpenShift={(shift) => navigate(`/admin/shifts/${shift.id}`)}
      />

      <Dialog open={deleteDlg.open} onOpenChange={(o) => !o && setDeleteDlg((d) => ({ ...d, open: false }))}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Permanently delete {deleteDlg.label || 'this job'}?</DialogTitle>
            <DialogDescription>
              This cannot be undone. Jobs with worked time, approved timesheets, or settlement
              history are protected and will be skipped (archive those instead).
            </DialogDescription>
          </DialogHeader>
          {deleteDlg.loading && !deleteDlg.impact ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
          ) : (
            <div className="space-y-3 text-sm">
              {deleteDlg.impact && deleteDlg.impact.total_assigned_talent > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-800 p-3" data-testid="assigned-talent-warning">
                  <b>{deleteDlg.impact.total_assigned_talent} talent still assigned.</b> Deleting will
                  cancel their assignment(s) and automatically notify each of them. Choose “Go back”
                  to remove the talent manually first.
                </div>
              )}
              {deleteDlg.impact && deleteDlg.impact.blocked_count > 0 && (
                <div className="rounded-md border border-slate-300 bg-slate-50 text-slate-700 p-3">
                  {deleteDlg.impact.blocked_count} selected job(s) have worked/settlement history and
                  will be skipped.
                </div>
              )}
              <div>
                <Label className="text-xs">Reason (recorded in the audit log)</Label>
                <Textarea
                  value={deleteDlg.reason}
                  onChange={(e) => setDeleteDlg((d) => ({ ...d, reason: e.target.value }))}
                  placeholder="e.g. duplicate of corrected posting"
                  rows={2}
                  data-testid="delete-reason-input"
                />
              </div>
              <div>
                <Label className="text-xs">Type DELETE to confirm</Label>
                <Input
                  value={deleteDlg.confirmText}
                  onChange={(e) => setDeleteDlg((d) => ({ ...d, confirmText: e.target.value }))}
                  placeholder="DELETE"
                  data-testid="delete-confirm-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDlg((d) => ({ ...d, open: false }))} data-testid="delete-cancel-btn">
              Go back
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={deleteDlg.loading || deleteDlg.confirmText !== 'DELETE'}
              onClick={confirmDeleteNow}
              data-testid="delete-confirm-submit-btn"
            >
              {deleteDlg.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {inviteModal && (
        <InviteTalentModal
          open={!!inviteModal}
          onOpenChange={(v) => { if (!v) setInviteModal(null); }}
          shiftId={inviteModal.shiftId}
          shiftMeta={inviteModal.shiftMeta}
          initialAsBackup={inviteModal.asBackup}
        />
      )}
    </div>
  );
};

const EmptyState = ({ diag, error, onRetry, totalShiftsLoaded, activeFilters, onClearFilters }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12 text-center" data-testid="empty-state">
    <CalIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
    {error ? (
      <>
        <h3 className="font-bold text-rose-700 text-lg">Failed to load jobs</h3>
        <p className="text-sm text-slate-500 mt-1 mb-4">
          {error.status ? `HTTP ${error.status}` : 'Network error'} — {error.message}
        </p>
      </>
    ) : totalShiftsLoaded > 0 && activeFilters ? (
      <>
        <h3 className="font-bold text-slate-700 text-lg">No events match the current filters</h3>
        <p className="text-sm text-slate-500 mt-1 mb-4">
          {totalShiftsLoaded} shift{totalShiftsLoaded !== 1 ? 's' : ''} loaded — they're hidden by an active filter, not gone. Clear filters to see all your jobs.
        </p>
        {onClearFilters && (
          <button
            onClick={onClearFilters}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-cyan-600 text-white text-sm font-bold hover:bg-cyan-700"
            data-testid="empty-clear-all-filters-btn"
          >
            Clear All Filters
          </button>
        )}
      </>
    ) : diag ? (
      <>
        <h3 className="font-bold text-amber-700 text-lg">API returned no shifts</h3>
        <p className="text-sm text-slate-500 mt-1 mb-3">
          The /api/admin/shifts endpoint responded successfully but with zero rows. This is unusual if you have active events.
        </p>
        <details className="text-left bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 max-w-xl mx-auto">
          <summary className="text-xs font-bold text-slate-600 cursor-pointer">Diagnostic info (share with support)</summary>
          <pre className="text-[10px] text-slate-500 mt-2 overflow-x-auto whitespace-pre-wrap break-all">
{JSON.stringify(diag, null, 2)}
          </pre>
        </details>
      </>
    ) : (
      <>
        <h3 className="font-bold text-slate-700 text-lg">No events in this bucket</h3>
        <p className="text-sm text-slate-500 mt-1 mb-4">Try a different time bucket or clear filters.</p>
      </>
    )}
    <button
      onClick={onRetry}
      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl shadow-sm"
      data-testid="empty-state-retry"
    >
      <RefreshCw className="w-4 h-4" /> Reload jobs
    </button>
  </div>
);

/* ------------------------------------------------------- list view --- */

const ListView = ({ clientGroups, events, groupBy, onOpen, onRetry }) => {
  if (events.length === 0) return <EmptyState onRetry={onRetry} />;
  const rows = groupBy === 'client'
    ? clientGroups.flatMap(g => [{ kind: 'header', group: g }, ...g.items.map(e => ({ kind: 'row', event: e }))])
    : events.map(e => ({ kind: 'row', event: e }));
  return (
    <div className="divide-y divide-slate-100" data-testid="list-view">
      {rows.map((r, i) => r.kind === 'header' ? (
        <div key={`h-${i}`} className="px-4 py-2.5 bg-slate-50 flex items-center gap-3">
          <ClientBadge name={r.group.client_name} logoUrl={r.group.client_logo_url} />
          <div className="flex-1">
            <div className="font-bold text-slate-900 text-sm">{r.group.client_name}</div>
            <div className="text-[11px] text-slate-500">{r.group.total_events} events · {r.group.total_filled}/{r.group.total_needed} staffed</div>
          </div>
        </div>
      ) : (
        <button key={r.event.key} onClick={() => onOpen(r.event)} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3" data-testid={`list-row-${r.event.key}`}>
          <div className="text-right shrink-0 w-14">
            <div className="text-[10px] uppercase font-bold text-slate-400">{(parseLocalDate(r.event.first_date) || new Date()).toLocaleDateString('en-US', { month: 'short' })}</div>
            <div className="text-lg font-extrabold text-slate-800 leading-none">
              {(parseLocalDate(r.event.first_date) || new Date()).getDate()}
              {r.event.is_multi_day ? `–${(parseLocalDate(r.event.last_date) || new Date()).getDate()}` : ''}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-cyan-700 uppercase tracking-wide truncate">{r.event.client_name}</div>
            <div className="text-sm font-bold text-slate-900 truncate">{r.event.event_title}</div>
            <div className="text-[11px] text-slate-500 truncate">{r.event.role_name} · {r.event.city}{r.event.state ? `, ${r.event.state}` : ''}</div>
          </div>
          <div className="hidden md:block text-right text-xs">
            <span className={`font-bold text-${fillTone(r.event.filled, r.event.needed)}-700`}>{r.event.filled}/{r.event.needed}</span>
            <div className="text-[10px] text-slate-400">staffed</div>
          </div>
          {r.event.applicants > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
              {r.event.applicants} apps
            </span>
          )}
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </button>
      ))}
    </div>
  );
};

/* ----------------------------------------------------- calendar view -- */

function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x; }
function dayOfWeekMon(d) { const x = d.getDay(); return (x + 6) % 7; } // Mon=0 .. Sun=6
function startOfWeek(d) { const x = new Date(d); x.setDate(x.getDate() - dayOfWeekMon(x)); x.setHours(0,0,0,0); return x; }
function isoOf(d) { return d.toISOString().slice(0, 10); }

function eventsOn(events, d) {
  const iso = isoOf(d);
  return events.filter(e => (e.first_date || '') <= iso && (e.last_date || '') >= iso);
}

function statusDot(e) {
  if (e.applicants > 0) return 'bg-violet-500';
  if (e.needed > 0 && e.filled >= e.needed) return 'bg-emerald-500';
  if (e.needed > 0 && e.filled >= e.needed * 0.6) return 'bg-amber-500';
  if (e.needed > 0) return 'bg-rose-500';
  return 'bg-slate-400';
}

// Iter-230 #8b — pick the dominant status for a given day's events so we
// can shade the entire calendar cell instead of relying on tiny dots.
// Priority: in_progress > staffing_needed > reviewing > partial > fully_staffed > completed.
function dominantDayStatus(dayEvents) {
  if (!dayEvents.length) return null;
  const rank = {
    in_progress: 6, staffing_needed: 5, reviewing: 4,
    partially_staffed: 3, ready: 2, fully_staffed: 1, completed: 0,
  };
  let best = null;
  for (const e of dayEvents) {
    const s = eventStatus(e);
    if (!best || (rank[s.id] || 0) > (rank[best.id] || 0)) best = s;
  }
  return best;
}
function dayCellTint(status) {
  if (!status) return '';
  const tone = status.tone;
  // Subtle wash so the existing chips still pop, but the date is at-a-glance.
  return `bg-${tone}-50/80`;
}

const EventChip = ({ event, compact, onOpen }) => {
  const dot = statusDot(event);
  const initials = (event.client_name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  return (
    <button
      onClick={() => onOpen(event)}
      className="w-full text-left bg-white hover:bg-slate-50 border border-slate-200 hover:border-cyan-300 rounded-lg px-1.5 py-1 flex items-center gap-1.5 transition group"
      title={`${event.client_name} · ${event.event_title}`}
      data-testid={`cal-chip-${event.key}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot} shrink-0`} />
      <span className="text-[9px] font-bold text-cyan-700 shrink-0">{initials}</span>
      <span className="text-[10px] text-slate-700 font-medium truncate flex-1">
        {event.event_title}
      </span>
      {!compact && event.applicants > 0 && (
        <span className="text-[9px] font-bold text-violet-600 shrink-0">·{event.applicants}</span>
      )}
    </button>
  );
};

/* --- Month view ---------------------------------------------------- */

const MonthGrid = ({ anchor, events, onOpen, onJumpToDay }) => {
  const first = startOfMonth(anchor);
  const gridStart = new Date(first); gridStart.setDate(first.getDate() - dayOfWeekMon(first));
  const days = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d;
  });
  const inThisMonth = (d) => d.getMonth() === anchor.getMonth();

  return (
    <div className="grid grid-cols-7 bg-slate-100" style={{ gap: '1px' }} data-testid="calendar-month-grid">
      {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
        <div key={d} className="px-3 py-2 bg-slate-50 text-[10px] uppercase font-bold tracking-wider text-slate-500">{d}</div>
      ))}
      {days.map((d, i) => {
        const iso = isoOf(d);
        const dayEvents = eventsOn(events, d);
        const shown = dayEvents.slice(0, 3);
        const overflow = dayEvents.length - shown.length;
        const isToday = iso === TODAY;
        const inMonth = inThisMonth(d);
        const tintClass = dayCellTint(dominantDayStatus(dayEvents));
        return (
          <button
            key={i}
            onClick={() => onJumpToDay(new Date(d))}
            className={`relative min-h-[100px] md:min-h-[120px] p-1.5 text-left transition
              ${isToday ? 'bg-cyan-100/80 ring-2 ring-cyan-500 ring-inset z-10' : tintClass || 'bg-white hover:bg-slate-50'}
              ${!inMonth ? 'opacity-50' : ''}`}
            data-testid={`cal-day-${iso}`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs font-semibold
                ${isToday ? 'text-cyan-700' : inMonth ? 'text-slate-800' : 'text-slate-300'}`}>
                {isToday ? (
                  <span className="inline-flex items-center gap-1">
                    <span className="w-5 h-5 rounded-full bg-cyan-600 text-white grid place-items-center text-[10px] font-bold">{d.getDate()}</span>
                  </span>
                ) : d.getDate()}
              </span>
              {dayEvents.length > 0 && (
                <span className={`text-[9px] font-bold ${isToday ? 'text-cyan-700' : 'text-slate-400'}`}>
                  {dayEvents.length}
                </span>
              )}
            </div>
            <div className="space-y-1">
              {shown.map(e => (
                <span key={e.key} onClick={(ev) => { ev.stopPropagation(); onOpen(e); }} className="block">
                  <EventChip event={e} compact onOpen={onOpen} />
                </span>
              ))}
              {overflow > 0 && (
                <div className="text-[10px] font-semibold text-slate-500 px-1.5">+{overflow} more</div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

/* --- Week view ----------------------------------------------------- */

const WeekGrid = ({ anchor, events, onOpen, onJumpToDay }) => {
  const start = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => { const x = new Date(start); x.setDate(start.getDate() + i); return x; });
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 bg-slate-100" style={{ gap: '1px' }} data-testid="calendar-week-grid">
      {days.map((d, i) => {
        const iso = isoOf(d);
        const dayEvents = eventsOn(events, d);
        const isToday = iso === TODAY;
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        return (
          <div
            key={i}
            className={`min-h-[260px] p-3 flex flex-col
              ${isToday ? 'bg-cyan-50/70 ring-1 ring-cyan-400 ring-inset z-10' : isWeekend ? 'bg-slate-50/60' : 'bg-white'}`}
            data-testid={`cal-week-day-${iso}`}
          >
            <button onClick={() => onJumpToDay(new Date(d))} className="flex items-baseline gap-2 mb-2 hover:opacity-80 text-left">
              <span className={`text-[10px] uppercase font-bold tracking-wider ${isToday ? 'text-cyan-600' : 'text-slate-500'}`}>
                {d.toLocaleDateString('en-US', { weekday: 'short' })}
              </span>
              <span className={`text-base font-bold ${isToday ? 'text-cyan-700' : 'text-slate-900'}`}>{d.getDate()}</span>
              {isToday && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-cyan-600 text-white">TODAY</span>}
            </button>
            <div className="space-y-1.5 flex-1">
              {dayEvents.length === 0 ? (
                <div className="text-[10px] text-slate-300 italic">No events</div>
              ) : dayEvents.map(e => (
                <EventChip key={e.key} event={e} onOpen={onOpen} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* --- Day view ------------------------------------------------------ */

const DayGrid = ({ anchor, events, onOpen }) => {
  const iso = isoOf(anchor);
  const dayEvents = eventsOn(events, anchor);
  const isToday = iso === TODAY;
  return (
    <div className="p-4 md:p-6 bg-white" data-testid="calendar-day-grid">
      <div className="flex items-baseline gap-3 mb-5">
        <div className={`w-14 h-14 rounded-2xl grid place-items-center font-bold shrink-0
          ${isToday ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-800'}`}>
          <div className="text-center leading-none">
            <div className="text-[10px] uppercase tracking-wide">{anchor.toLocaleDateString('en-US', { month: 'short' })}</div>
            <div className="text-2xl font-extrabold mt-0.5">{anchor.getDate()}</div>
          </div>
        </div>
        <div>
          <div className={`text-sm font-bold uppercase tracking-wide ${isToday ? 'text-cyan-700' : 'text-slate-700'}`}>
            {anchor.toLocaleDateString('en-US', { weekday: 'long' })}{isToday ? ' · TODAY' : ''}
          </div>
          <div className="text-xs text-slate-500">{anchor.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-2xl font-extrabold text-slate-900">{dayEvents.length}</div>
          <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">events</div>
        </div>
      </div>
      {dayEvents.length === 0 ? (
        <div className="text-center text-slate-400 py-12 italic text-sm border border-dashed border-slate-200 rounded-xl">
          No events scheduled for this day.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {dayEvents.map(e => (
            <button key={e.key} onClick={() => onOpen(e)}
                    className="text-left bg-white hover:bg-slate-50 border border-slate-200 hover:border-cyan-300 rounded-xl p-3 transition shadow-sm"
                    data-testid={`cal-day-event-${e.key}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`w-2 h-2 rounded-full ${statusDot(e)}`} />
                <span className="text-[10px] uppercase font-bold tracking-wider text-cyan-700">{e.client_name}</span>
              </div>
              <div className="font-bold text-slate-900 text-sm truncate">{e.event_title}</div>
              <div className="text-xs text-slate-500 truncate">{e.role_name} · {[e.city, e.state].filter(Boolean).join(', ')}</div>
              {(() => {
                const todayShift = e.shifts.find(s => s.shift_date === isoOf(anchor)) || e.shifts[0];
                return todayShift?.start_time && todayShift?.end_time ? (
                  <div className="text-xs text-cyan-700 font-semibold mt-1">
                    {formatTimeRange(todayShift.start_time, todayShift.end_time)}
                  </div>
                ) : null;
              })()}
              <div className="mt-2 flex items-center gap-3 text-[11px] font-semibold">
                <span className={`text-${fillTone(e.filled, e.needed)}-700`}>{e.filled}/{e.needed || '?'} staffed</span>
                {e.applicants > 0 && <span className="text-violet-700">{e.applicants} to review</span>}
                {e.hourly_rate && <span className="text-slate-500 ml-auto">${e.hourly_rate}/hr</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* --- Calendar shell ------------------------------------------------ */

const CalendarView = ({ events, onOpen }) => {
  const [mode, setMode] = useState('month'); // month | week | day
  const [anchor, setAnchor] = useState(new Date());

  const label = useMemo(() => {
    if (mode === 'month') return anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (mode === 'week') {
      const s = startOfWeek(anchor);
      const e = new Date(s); e.setDate(s.getDate() + 6);
      const sameMonth = s.getMonth() === e.getMonth();
      const sFmt = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const eFmt = sameMonth ? e.getDate() : e.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${sFmt} – ${eFmt}, ${e.getFullYear()}`;
    }
    return anchor.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
  }, [anchor, mode]);

  const step = (dir) => {
    const d = new Date(anchor);
    if (mode === 'month') d.setMonth(d.getMonth() + dir);
    else if (mode === 'week') d.setDate(d.getDate() + 7 * dir);
    else d.setDate(d.getDate() + dir);
    setAnchor(d);
  };
  const goToday = () => setAnchor(new Date());
  const jumpToDay = (d) => { setAnchor(d); setMode('day'); };

  return (
    <div data-testid="calendar-view">
      {/* Calendar header */}
      <div className="flex flex-wrap items-center gap-2 px-4 md:px-5 py-3.5 border-b border-slate-100 bg-white">
        <div className="text-base md:text-lg font-bold text-slate-900 mr-auto">{label}</div>

        {/* Mode toggle */}
        <div className="bg-slate-100 rounded-xl p-1 flex text-xs font-semibold">
          <button onClick={() => setMode('day')}   className={`px-3 py-1.5 rounded-lg transition ${mode === 'day'   ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`} data-testid="cal-mode-day">Day</button>
          <button onClick={() => setMode('week')}  className={`px-3 py-1.5 rounded-lg transition ${mode === 'week'  ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`} data-testid="cal-mode-week">Week</button>
          <button onClick={() => setMode('month')} className={`px-3 py-1.5 rounded-lg transition ${mode === 'month' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`} data-testid="cal-mode-month">Month</button>
        </div>

        {/* Nav */}
        <div className="flex items-center gap-1.5">
          <button onClick={() => step(-1)}  className="w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:border-slate-300 grid place-items-center" data-testid="cal-prev">‹</button>
          <button onClick={goToday}         className="text-xs font-semibold text-slate-700 border border-slate-200 rounded-lg px-3 h-8 hover:border-slate-300" data-testid="cal-today">Today</button>
          <button onClick={() => step(1)}   className="w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:border-slate-300 grid place-items-center" data-testid="cal-next">›</button>
        </div>
      </div>

      {/* Legend */}
      <div className="hidden md:flex items-center gap-3 px-5 py-2 border-b border-slate-100 bg-slate-50/40 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Fully staffed</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> Partial</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500" /> Unfilled</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-500" /> Applicants pending</span>
      </div>

      {/* Body */}
      {mode === 'month' && <MonthGrid anchor={anchor} events={events} onOpen={onOpen} onJumpToDay={jumpToDay} />}
      {mode === 'week'  && <WeekGrid  anchor={anchor} events={events} onOpen={onOpen} onJumpToDay={jumpToDay} />}
      {mode === 'day'   && <DayGrid   anchor={anchor} events={events} onOpen={onOpen} />}
    </div>
  );
};

export default AdminJobsV2;
